/* eslint-disable require-jsdoc */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Viewer } from '../library/viewer'
import { fetchProductData } from './dataRetrieval'
import { renderMaterialOptions } from './materialOptions'
import { get_doc_title } from './common'

const isTouch = () => {
  if ('maxTouchPoints' in navigator) return navigator.maxTouchPoints > 0

  if ('msMaxTouchPoints' in navigator) return navigator.msMaxTouchPoints > 0

  const mQ = window.matchMedia?.('(any-pointer: coarse)')
  if (mQ?.media === '(any-pointer: coarse)') return mQ.matches

  return 'ontouchstart' in window
}

// Fetch hook met cancellation als er snel een nieuwe set opgevraagd zou worden
const useProductData = (modelId) => {
  const [productData, setProductData] = useState([])

  useEffect(() => {
    let cancelled = false
    fetchProductData(modelId)
      .then((data) => {
        if (!cancelled) setProductData(data || [])
      })
      .catch(() => {
        if (!cancelled) setProductData([])
      })
    return () => {
      cancelled = true
    }
  }, [modelId])

  return productData
}

// wrapper voor MaterialOptions om niet teveel hooks te callen in React
function MaterialOptionsWrapper({
  product,
  selectedProductVariant,
  onUpdate,
  onToggle,
}) {
  return renderMaterialOptions(
    product,
    selectedProductVariant,
    onUpdate,
    onToggle,
  )
}

function ModelDetail() {
  const { modelId } = useParams()
  const productData = useProductData(modelId)
  const [arAPI, setArAPI] = useState(null)
  const viewerAPIRef = useRef(null)

  // doc title
  useEffect(() => {
    document.title = get_doc_title() + ` - ${modelId}`
  }, [])

  // actieve variant
  const [selectedProductVariant, setSelectedProductVariant] = useState([])

  // hidden xsells door toggle
  const [hiddenVariants, setHiddenVariants] = useState([]) // array van modelCode strings

  const [environment, setEnvironment] = useState('brown_photostudio_05_1k')
  const [textureResolution, setTextureResolution] = useState('medium')
  const [renderResolution, setRenderResolution] = useState('4k')
  const [renderTemplate, setRenderTemplate] = useState('2')
  const [viewerError, setViewerError] = useState(null)
  const [viewerReady, setViewerReady] = useState(false)

  const send_productdata =
    typeof window !== 'undefined' &&
    typeof window.PUPPETEER_DATA !== 'undefined' &&
    typeof window.PUPPETEER_DATA.engine !== 'undefined' &&
    window.PUPPETEER_DATA.engine === 'blender'

  // viewer error handler
  const handleViewerError = useCallback((error) => {
    console.error('Viewer error:', error)
    setViewerError(error)
  }, [])

  // viewer ready handler
  const handleViewerReady = useCallback((data) => {
    console.log('🕺 Viewer ready!!', data)
    setViewerReady(true)
    if (
      typeof window !== 'undefined' &&
      typeof window.PUPPETEER_DATA !== 'undefined'
    ) {
      window.PUPPETEER_DATA.ready_data = structuredClone(data)
      window.dispatchEvent(
        new CustomEvent('viewerReady', {
          detail: window.PUPPETEER_DATA,
        }),
      )
    }
  }, [])

  // viewer capture handler
  const handleViewerCapture = useCallback(
    (pixels) => {
      console.log('📸 Viewer captured')

      const link = document.createElement('a')
      link.href = pixels
      link.download = `${modelId}_captured_image.jpg`
      link.click()
    },
    [modelId],
  )

  // hi-res export
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState(null)
  const [exportLabel, setExportLabel] = useState(null)

  const handleHiResRequest = useCallback(async () => {
    try {
      const delay = (ms) => new Promise((r) => setTimeout(r, ms))

      const parseTimeToSeconds = function (mmss) {
        const [mm, ss] = mmss.split(':')
        return Number(mm) * 60 + Number(ss)
      }

      const pad2 = function (digit) {
        return String(digit).padStart(2, '0')
      }

      const makeStamp = function (d = new Date()) {
        const year = d.getFullYear()
        const month = pad2(d.getMonth() + 1)
        const day = pad2(d.getDate())
        const hour = pad2(d.getHours())
        const minutes = pad2(d.getMinutes())

        return `${year}${month}${day}_${hour}${minutes}`
      }

      const api = viewerAPIRef.current
      if (!api || !api.isReady) {
        return
      }
      const snapshot = api.getDataSnapshot()

      setIsExporting(true)
      setExportError(null)
      setExportLabel('Requesting render...')

      let base_ssr_url = 'https://rbt-ssr.visualsforbrands.com'

      // doe eerst een run
      const response = await fetch(`${base_ssr_url}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Runpod-Owner': 'vfb',
        },
        body: JSON.stringify({
          size: renderResolution,
          filetype: 'jpeg',
          shadows: true,
          snapshot: snapshot,
          scene_template: parseInt(renderTemplate),
        }),
      })

      if (!response.ok) {
        const msg = await response.text().catch(() => '')
        throw new Error(msg || `Export failed (${response.status})`)
      }

      // json terugkrijgen van de run
      const run_json = await response.json()

      if (!run_json.id) {
        throw new Error(`Export failed (no run feedback)`)
      }

      let run_status = false
      let run_stop_statusses = ['COMPLETED', 'FAILED']
      let run_output = false
      let run_percent_set = false

      setExportLabel('In render queue...')

      while (!run_stop_statusses.includes(run_status)) {
        let status_response = await fetch(
          `${base_ssr_url}/status/${run_json.id}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Runpod-Owner': 'vfb',
            },
          },
        )
        if (!status_response.ok) {
          const msg = await status_response.text().catch(() => '')
          throw new Error(
            msg ||
              `Export failed - status fetch failed (${status_response.status})`,
          )
        }
        let status_json = await status_response.json()
        if (!status_json.status) {
          throw new Error(`Export failed (no status feedback)`)
        }
        run_status = status_json.status
        if (run_status === 'IN_PROGRESS') {
          setExportLabel(run_percent_set ? 'Finalizing...' : 'In progress...')
          // percentages
          if (
            status_json?.output &&
            status_json.output.includes('Time') &&
            status_json.output.includes('Remaining')
          ) {
            const m = status_json.output.match(
              /Time:(\d{2}:\d{2}(?:\.\d+)?)\s*\|\s*Remaining:(\d{2}:\d{2}(?:\.\d+)?)/,
            )
            if (m) {
              const elapsed = parseTimeToSeconds(m[1])
              const remaining = parseTimeToSeconds(m[2])
              const total = elapsed + remaining

              run_percent_set = true

              const percent = total > 0 ? Math.ceil((elapsed / total) * 100) : 0

              if (percent !== 0) {
                setExportLabel(`In progress (${percent}%)`)
              }
            }
          }
        } else if (run_status === 'COMPLETED') {
          if (status_json?.output?.url) {
            run_output = status_json.output.url
          }
        } else if (run_status === 'FAILED') {
          if (status_json?.output) {
            run_output = status_json.output
          }
        }
        await delay(2000) // 2 sec wachten
      }

      if (run_status === 'COMPLETED') {
        if (run_output) {
          const download_response = await fetch(run_output)
          if (!download_response.ok)
            throw new Error(`Download failed: ${download_response.status}`)
          const blob = await download_response.blob()
          const blobUrl = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = blobUrl
          a.download = `${modelId}_${makeStamp()}.jpeg`
          document.body.appendChild(a)
          a.click()
          a.remove()
          URL.revokeObjectURL(blobUrl)
        } else {
          throw new Error(`Export failed (no url feedback)`)
        }
      }
      // failed
      else {
        console.error('Hi-res export failed:', run_output)
        setExportError('Export failed')
      }
    } catch (err) {
      console.error('Hi-res export failed:', err)
      setExportError(err?.message || 'Export failed')
    } finally {
      setIsExporting(false)
    }
  }, [modelId, renderResolution, renderTemplate])

  // default variants bij pageload en inladen van productData
  useEffect(() => {
    if (!productData.length) return

    const initVariants = async () => {
      const variants = await Promise.all(productData.map(createInitialVariant))
      setSelectedProductVariant(variants)

      // reset checks
      const nodes = document.querySelectorAll?.('.component-data') || []
      nodes.forEach((node) => node.classList?.add?.('checked'))

      // je krijgt de hiddenVariants mee via PUPPETEER, bv. hiddenXsells: [ 'PILFOLF' ]
      if (
        typeof window !== 'undefined' &&
        typeof window.PUPPETEER_DATA !== 'undefined' &&
        typeof window.PUPPETEER_DATA.hiddenXsells !== 'undefined'
      ) {
        setHiddenVariants(window.PUPPETEER_DATA.hiddenXsells)
      } else {
        setHiddenVariants([]) // leeg by default
      }
    }

    initVariants()
  }, [productData])

  // maak een eerste variant voor een product + Puppeteer overrides
  const createInitialVariant = async (product) => {
    const initialVariant = {
      modelCode: product.modelCode,
      materialData: [],
      crossSellData: [],
    }

    if (product.materialData) {
      initialVariant.materialData = Object.values(
        product.materialData.reduce((acc, material) => {
          let can_add = true
          if (
            typeof window !== 'undefined' &&
            typeof window.PUPPETEER_DATA !== 'undefined' &&
            typeof window.PUPPETEER_DATA.materials !== 'undefined' &&
            (Object.keys(window.PUPPETEER_DATA.materials).includes(
              product.modelCode + '_' + material.type,
            ) ||
              Object.keys(window.PUPPETEER_DATA.materials).includes(
                material.type,
              ))
          ) {
            can_add = false

            const productKey =
              window.PUPPETEER_DATA.materials[
                product.modelCode + '_' + material.type
              ]
            const typeKey = window.PUPPETEER_DATA.materials[material.type]
            if (productKey === material.code || typeKey === material.code) {
              can_add = true
            }
          }

          if (can_add) {
            acc[material.type] = acc[material.type] || []
            acc[material.type].push(material)
          }
          return acc
        }, {}),
      ).map((group) => group.sort(() => 0.5 - Math.random())[0])
    }

    if (product.crossSellData) {
      initialVariant.crossSellData = product.crossSellData.map(
        (crossSellProduct) => {
          const crossSellVariant = {
            modelCode: crossSellProduct.modelCode,
            materialData: [],
          }

          if (crossSellProduct.materialData) {
            crossSellVariant.materialData = Object.values(
              crossSellProduct.materialData.reduce((acc, material) => {
                let can_add = true
                if (
                  typeof window !== 'undefined' &&
                  typeof window.PUPPETEER_DATA !== 'undefined' &&
                  typeof window.PUPPETEER_DATA.materials !== 'undefined' &&
                  (Object.keys(window.PUPPETEER_DATA.materials).includes(
                    crossSellProduct.modelCode + '_' + material.type,
                  ) ||
                    Object.keys(window.PUPPETEER_DATA.materials).includes(
                      material.type,
                    ))
                ) {
                  can_add = false

                  const productKey =
                    window.PUPPETEER_DATA.materials[
                      crossSellProduct.modelCode + '_' + material.type
                    ]
                  const typeKey = window.PUPPETEER_DATA.materials[material.type]
                  if (
                    productKey === material.code ||
                    typeKey === material.code
                  ) {
                    can_add = true
                  }
                }

                if (can_add) {
                  acc[material.type] = acc[material.type] || []
                  acc[material.type].push(material)
                }
                return acc
              }, {}),
            ).map((group) => group.sort(() => 0.5 - Math.random())[0])
          }
          return crossSellVariant
        },
      )
    }

    return initialVariant
  }

  // update material handler, alleen als de container checked heeft als class
  const handleMaterialUpdate = useCallback(
    (event, type, material, modelCode) => {
      event?.preventDefault?.()
      const wrapper = event?.target?.closest?.('.component-data')
      if (wrapper && wrapper.classList?.contains('checked')) {
        try {
          console.log(
            '☝️ User clicked:',
            type,
            material.id,
            material.code,
            modelCode,
          )
          const newMaterialData = material
          setSelectedProductVariant((prev) => {
            const updated = structuredClone(prev)
            findAndUpdateMaterialData(modelCode, type, newMaterialData, updated)
            return updated
          })
        } catch (error) {
          console.error('❌ Error updating material data:', error)
        }
      } else {
        console.log('❌ Clicked trigger blocked')
      }
    },
    [],
  )

  // xsell toggle om te tonen of niet, handler
  const handleToggleComponent = useCallback((componentCode, include) => {
    setHiddenVariants((prevHidden) => {
      const set = new Set(prevHidden)
      if (!include) set.add(componentCode)
      else set.delete(componentCode)
      return Array.from(set)
    })
  }, [])

  // vind & update materials in geneste structuren
  const findAndUpdateMaterialData = (
    modelCode,
    type,
    newMaterialData,
    variants,
  ) => {
    for (const variant of variants) {
      if (variant.modelCode === modelCode) {
        variant.materialData = variant.materialData.map((m) =>
          m.type === type ? newMaterialData : m,
        )
        return true
      }
      if (
        variant.crossSellData &&
        findAndUpdateMaterialData(
          modelCode,
          type,
          newMaterialData,
          variant.crossSellData,
        )
      ) {
        return true
      }
    }
    return false
  }

  // filter de data die effectief naar de viewer gaat en houd rekening met hidden xsells
  const filteredSelectedProductVariant = useMemo(() => {
    if (!selectedProductVariant.length) return []
    if (!hiddenVariants.length) return selectedProductVariant
    const hidden = new Set(hiddenVariants)
    const cloned = structuredClone(selectedProductVariant)
    if (cloned[0]?.crossSellData?.length) {
      cloned[0].crossSellData = cloned[0].crossSellData.filter(
        (xsell) => !hidden.has(xsell.modelCode),
      )
    }
    return cloned
  }, [selectedProductVariant, hiddenVariants])

  // shadows var met Puppeteer override, default true
  const shadows = useMemo(() => {
    if (
      typeof window !== 'undefined' &&
      typeof window.PUPPETEER_DATA !== 'undefined' &&
      typeof window.PUPPETEER_DATA.shadows !== 'undefined'
    ) {
      return window.PUPPETEER_DATA.shadows
    }
    return true
  }, [])

  if (!productData.length) {
    return <div className="loading-screen">Waiting for API...</div>
  }

  return (
    <>
      {!viewerReady && (
        <div className="loading-screen">Downloading assets...</div>
      )}
      <div
        id="canvas-container"
        style={{
          display: 'flex',
          flexDirection: 'row',
          maxHeight: '100vh',
          position: 'relative',
        }}
      >
        {viewerError ? (
          <div className="viewer-error">Error: {viewerError}</div>
        ) : (
          <>
            <Viewer
              productData={filteredSelectedProductVariant}
              resolution={textureResolution}
              onViewerReady={handleViewerReady}
              onViewerError={handleViewerError}
              environment={environment}
              className="viewer"
              containerClassName="viewer-container"
              onCapture={
                typeof window !== 'undefined' &&
                typeof window.PUPPETEER_DATA !== 'undefined' &&
                typeof window.PUPPETEER_DATA.capture !== 'undefined'
                  ? handleViewerCapture
                  : false
              }
              castShadow={shadows}
              onARExport={setArAPI}
              needDataOnViewerReady={send_productdata}
              onViewerAPI={(api) => {
                viewerAPIRef.current = api
              }}
            />
          </>
        )}
        <div id="product-details">
          <div
            className="product-details-selects"
            style={{
              display: 'flex',
              justifyContent: 'space-evenly',
              width: '100%',
            }}
          >
            <span>
              <p>Pick a resolution:</p>
              <select
                value={textureResolution}
                onChange={(e) => setTextureResolution(e.target.value)}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="extra-large">Extra Large</option>
              </select>
            </span>

            <span>
              <p>Pick an environment:</p>
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
              >
                <option value="brown_photostudio_05_1k">
                  Brown Photostudio 05
                </option>
                <option value="studio">Studio</option>
                <option value="apartment">Apartment</option>
                <option value="brown_photostudio_01_1k">
                  Brown Photostudio 01
                </option>
              </select>
            </span>
          </div>

          {productData.map((product) => (
            <div key={product.modelCode}>
              <MaterialOptionsWrapper
                product={product}
                selectedProductVariant={selectedProductVariant}
                onUpdate={handleMaterialUpdate}
                onToggle={handleToggleComponent}
              />
            </div>
          ))}
        </div>
      </div>

      {viewerReady && isTouch() && (
        <button
          className="floating-button ar-export-button"
          onClick={() => arAPI?.exportAR()}
          disabled={!arAPI}
        >
          Export for AR
        </button>
      )}

      {viewerReady && (
        <div className="floating-export-panel">
          <button
            className="runpod-export-button"
            onClick={handleHiResRequest}
            disabled={isExporting}
          >
            {isExporting ? exportLabel : 'Render hi-res'}
            <span className="render-loader">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                aria-label="Loading"
                role="img"
                className="spinner"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="62.8"
                  strokeDashoffset="47.1"
                />
              </svg>
            </span>
          </button>
          <select
            className="render-resolution"
            value={renderResolution}
            onChange={(e) => setRenderResolution(e.target.value)}
          >
            <option value="medium">1K</option>
            <option value="large">2K</option>
            <option value="4k">4K</option>
            <option value="6k">6K</option>
          </select>
          <select
            className="render-template"
            value={renderTemplate}
            onChange={(e) => setRenderTemplate(e.target.value)}
          >
            <option value="2">Template 2</option>
            <option value="1">Template 1</option>
          </select>
          {exportError && (
            <div className="export-error">Error: {exportError}</div>
          )}
        </div>
      )}
    </>
  )
}

export default ModelDetail
